// pages/UsersPage.js
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import api from "../utils/axiosInstance";
import { useAppAuth } from "../context/AuthContext";

const UsersPage = () => {
  const { appUser } = useAppAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const [roleOpen, setRoleOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      showMessage("Failed to fetch users", "error");
      console.error("Fetch users failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    try {
      await api.post("/admin/users/invite", { email: inviteEmail });
      setInviteOpen(false);
      setInviteEmail("");
      fetchUsers();
      showMessage("Invitation sent successfully!");
    } catch (err) {
      showMessage("Failed to send invite", "error");
      console.error("Invite failed", err);
    }
  };

  const handleRoleUpdate = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}/role`, { role: newRole });
      setRoleOpen(false);
      fetchUsers();
      showMessage("Role updated successfully!");
    } catch (err) {
      showMessage("Failed to update role", "error");
      console.error("Role update failed", err);
    }
  };

  const handleTogglePremium = async (user) => {
    try {
      // Optimistically update UI
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_premium: !u.is_premium } : u
        )
      );

      await api.put(`/admin/users/${user.id}/premium`, {
        is_premium: !user.is_premium,
      });

      showMessage(
        `Premium ${!user.is_premium ? "granted" : "removed"} for ${user.email}!`
      );
    } catch (err) {
      // Rollback on error
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_premium: user.is_premium } : u))
      );
      showMessage("Failed to update premium status", "error");
      console.error("Toggle premium failed", err);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      fetchUsers();
      showMessage("User deleted successfully!");
    } catch (err) {
      showMessage("Failed to delete user", "error");
      console.error("Delete failed", err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Manage Users</Typography>
        {appUser.role === "super_admin" && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInviteOpen(true)}
          >
            Invite User
          </Button>
        )}
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Premium</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.role}</TableCell>
              <TableCell>
                <Switch
                  checked={!!user.is_premium}
                  onChange={() => handleTogglePremium(user)}
                  disabled={appUser.role === "admin" && user.role !== "seller"}
                />
              </TableCell>
              <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
              <TableCell align="right">
                {appUser.role === "super_admin" && (
                  <IconButton
                    onClick={() => {
                      setSelectedUser(user);
                      setNewRole(user.role);
                      setRoleOpen(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                {(appUser.role === "super_admin" ||
                  (appUser.role === "admin" && user.role === "seller")) && (
                  <IconButton onClick={() => handleDelete(user)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button onClick={handleInvite} variant="contained">
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Edit Dialog */}
      <Dialog open={roleOpen} onClose={() => setRoleOpen(false)}>
        <DialogTitle>Update Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              <MenuItem value="seller">Seller</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="super_admin">Super Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleOpen(false)}>Cancel</Button>
          <Button onClick={handleRoleUpdate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
